import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  IconButton,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Delete } from '@mui/icons-material';
import { en } from '../translations/en';
import { usePermissions } from '../hooks/usePermissions';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import {
  SettingsSidebar,
  type SidebarGroup,
  type SidebarStandaloneItem,
} from '../components/settings/SettingsSidebar';
import { AccountContent } from '../components/settings/AccountContent';
import { SecurityContent } from '../components/settings/SecurityContent';
import { DelegatesTab } from '../components/settings/DelegatesTab';
import { SubscriptionTab } from '../components/SubscriptionTab';
import api from '../utils/api';
import { logger } from '../utils/logger';

interface LegacyContact {
  name: string;
  email: string;
  relationship: string;
  instructions: string;
}

type SettingsView = 'account' | 'security' | 'subscription' | 'legacy-contact' | 'delegates';

export const Settings = () => {
  const theme = useTheme();
  const { permissions } = usePermissions();

  const isViewer = permissions?.role === 'viewer';
  const isDelegate = permissions?.role === 'delegate';

  // Navigation state
  const [activeView, setActiveView] = useState<SettingsView>('account');
  const [activeSectionId, setActiveSectionId] = useState<string | null>('settings-profile');

  // Legacy Contact state (stays here until extracted in a follow-up)
  const [legacyContacts, setLegacyContacts] = useState<LegacyContact[]>([]);
  const [newLegacyContact, setNewLegacyContact] = useState<LegacyContact>({
    name: '',
    email: '',
    relationship: '',
    instructions: '',
  });
  const [legacyContactsSaving, setLegacyContactsSaving] = useState(false);
  const [legacyContactsError, setLegacyContactsError] = useState<string | null>(null);
  const [legacyContactsSuccess, setLegacyContactsSuccess] = useState(false);

  useEffect(() => {
    const fetchLegacyContacts = async () => {
      try {
        const response = await api.get('/api/users/me/legacy-contacts');
        setLegacyContacts(response.data.legacyContacts || []);
      } catch (error) {
        logger.warn('Failed to load legacy contacts', error);
      }
    };
    fetchLegacyContacts();
  }, []);

  const resetLegacyContactForm = () => ({
    name: '',
    email: '',
    relationship: '',
    instructions: '',
  });

  const handleNewLegacyContactChange =
    (field: keyof LegacyContact) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewLegacyContact((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const addLegacyContact = () => {
    if (newLegacyContact.name && newLegacyContact.email) {
      setLegacyContacts([...legacyContacts, newLegacyContact]);
      setNewLegacyContact(resetLegacyContactForm());
    }
  };

  const removeLegacyContact = (index: number) => {
    setLegacyContacts(legacyContacts.filter((_, i) => i !== index));
  };

  const handleSaveLegacyContacts = async () => {
    try {
      setLegacyContactsSaving(true);
      setLegacyContactsError(null);
      const pendingContact =
        newLegacyContact.name && newLegacyContact.email ? [newLegacyContact] : [];
      const allContacts = [...legacyContacts, ...pendingContact];
      await api.put('/api/users/me/legacy-contacts', { legacyContacts: allContacts });
      if (pendingContact.length > 0) {
        setLegacyContacts(allContacts);
        setNewLegacyContact(resetLegacyContactForm());
      }
      setLegacyContactsSuccess(true);
      setTimeout(() => setLegacyContactsSuccess(false), 5000);
    } catch (err) {
      setLegacyContactsError(
        err instanceof Error ? err.message : 'Failed to save legacy contacts'
      );
    } finally {
      setLegacyContactsSaving(false);
    }
  };

  // Sidebar configuration — filter items by role
  const accountItems = [
    { id: 'settings-profile', label: 'Profile' },
    ...(!isViewer && !isDelegate
      ? [{ id: 'settings-identifiers', label: 'Creator Identifiers' }]
      : []),
    { id: 'settings-image-protection', label: 'Image Protection' },
    { id: 'settings-marketing', label: 'Marketing' },
    { id: 'settings-delete-account', label: 'Delete Account' },
    { id: 'settings-legal', label: 'Legal' },
  ];

  const sidebarGroups: SidebarGroup[] = [
    {
      label: 'Account',
      items: accountItems,
    },
    {
      label: 'Security',
      items: [
        { id: 'settings-password', label: 'Password' },
        { id: 'settings-mfa', label: 'MFA' },
        { id: 'settings-devices', label: 'Trusted Devices' },
      ],
    },
  ];

  const standaloneItems: SidebarStandaloneItem[] = [
    { id: 'subscription', label: 'Subscription' },
    { id: 'legacy-contact', label: 'Legacy Contact', hidden: isViewer || isDelegate },
    { id: 'delegates', label: 'Delegates', hidden: isViewer || isDelegate },
  ];

  const viewToGroupIndex: Record<string, number> = { account: 0, security: 1 };
  const groupIndexToView: SettingsView[] = ['account', 'security'];

  const handleGroupClick = (groupIndex: number) => {
    const view = groupIndexToView[groupIndex];
    if (view) {
      setActiveView(view);
      const firstItem = sidebarGroups[groupIndex]?.items[0];
      if (firstItem) {
        setActiveSectionId(firstItem.id);
      }
    }
  };

  const handleSectionClick = (sectionId: string, groupIndex: number) => {
    const view = groupIndexToView[groupIndex];
    if (view) {
      setActiveView(view);
      setActiveSectionId(sectionId);
      // Scroll to the section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleStandaloneClick = (itemId: string) => {
    setActiveView(itemId as SettingsView);
    setActiveSectionId(null);
  };

  const activeGroupIndex = viewToGroupIndex[activeView] ?? -1;

  // Render content based on active view
  const renderContent = () => {
    switch (activeView) {
      case 'account':
        return <AccountContent />;
      case 'security':
        return <SecurityContent />;
      case 'subscription':
        return <SubscriptionTab />;
      case 'legacy-contact':
        return renderLegacyContact();
      case 'delegates':
        return <DelegatesTab />;
      default:
        return <AccountContent />;
    }
  };

  const renderLegacyContact = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Legacy Contact
      </Typography>
      <Typography variant="body2" paragraph color="text.secondary">
        Assign a trusted person to oversee your catalog in the future. Legacy Contacts ensure your
        body of work remains protected and accessible according to your wishes.
      </Typography>

      {legacyContacts.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <List>
            {legacyContacts.map((contact, index) => (
              <ListItem
                key={index}
                sx={{
                  bgcolor: 'background.paper',
                  mb: 2,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {contact.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contact.email}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {contact.relationship}
                      </Typography>
                    </Box>
                    <IconButton
                      edge="end"
                      onClick={() => removeLegacyContact(index)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                  {contact.instructions && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Instructions: &ldquo;{contact.instructions}&rdquo;
                    </Typography>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Assign Legacy Contact
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
        <TextField
          label="Full Name"
          value={newLegacyContact.name}
          onChange={handleNewLegacyContactChange('name')}
          fullWidth
          variant="outlined"
          required
        />
        <TextField
          label="Email Address"
          type="email"
          value={newLegacyContact.email}
          onChange={handleNewLegacyContactChange('email')}
          fullWidth
          variant="outlined"
          required
        />
        <TextField
          select
          label="Relationship"
          value={newLegacyContact.relationship}
          onChange={handleNewLegacyContactChange('relationship')}
          fullWidth
          variant="outlined"
        >
          <MenuItem value="spouse">Spouse</MenuItem>
          <MenuItem value="child">Child</MenuItem>
          <MenuItem value="parent">Parent</MenuItem>
          <MenuItem value="sibling">Sibling</MenuItem>
          <MenuItem value="attorney">Attorney</MenuItem>
          <MenuItem value="estate_executor">Estate Executor</MenuItem>
          <MenuItem value="trusted_friend">Trusted Friend</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </TextField>
        <TextField
          label="Legacy Instructions"
          value={newLegacyContact.instructions}
          onChange={handleNewLegacyContactChange('instructions')}
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          placeholder="Specific instructions for managing your catalog, wishes for preservation, access rights, etc..."
          helperText="Detailed instructions ensure your catalog is managed according to your wishes"
        />
        {legacyContactsSuccess && (
          <Typography color="success.main" sx={{ mb: 2, fontSize: '0.875rem' }}>
            Legacy plan saved successfully!
          </Typography>
        )}
        {legacyContactsError && (
          <Typography color="error.main" sx={{ mb: 2, fontSize: '0.875rem' }}>
            {legacyContactsError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addLegacyContact}
            disabled={!newLegacyContact.name || !newLegacyContact.email}
          >
            Assign Contact
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveLegacyContacts}
            disabled={
              legacyContactsSaving ||
              (!newLegacyContact.name &&
                !newLegacyContact.email &&
                legacyContacts.length === 0)
            }
            startIcon={
              legacyContactsSaving ? <CircularProgress size={16} /> : undefined
            }
          >
            {legacyContactsSaving ? 'Saving...' : 'Save Legacy Plan'}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        sx={{
          mb: 4,
          color: theme.palette.primary.main,
          fontFamily: 'Libre Baskerville, serif',
          fontWeight: 'bold',
        }}
      >
        {en.settings.title}
      </Typography>

      <SettingsLayout
        sidebar={
          <SettingsSidebar
            groups={sidebarGroups}
            standaloneItems={standaloneItems}
            activeGroupIndex={activeGroupIndex}
            activeSectionId={activeSectionId}
            onGroupClick={handleGroupClick}
            onSectionClick={handleSectionClick}
            onStandaloneClick={handleStandaloneClick}
          />
        }
      >
        {renderContent()}
      </SettingsLayout>
    </Container>
  );
};
